import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, GitBranch, FileCheck2, AlertTriangle, Share2, Cpu, Megaphone, Building2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Card, SectionTitle } from "@/components/common/PageHeader";
import { Loading, Bar } from "@/pages/_shared";
import api from "@/lib/api";

const STATUS_COLOR = {
  IMPLEMENTED: "#059669", FUNCTIONAL: "#059669", SUPPORTED: "#059669", RECORDED: "#059669", RESOLVED: "#059669",
  PARTIAL: "#0369a1", QUALIFIED: "#0369a1", OPEN: "#a16207", DOCUMENTED: "#a16207",
  "NOT STARTED": "#dc2626", "NOT CLAIMED": "#64748b", "NOT CONNECTED": "#dc2626",
  "PROVIDER APPROVAL PENDING": "#a16207", "COMING SOON": "#64748b", SANDBOX: "#c2410c", LIVE: "#059669",
};

function Chip({ s }) {
  const c = STATUS_COLOR[s] || "#64748b";
  return <span className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider"
    style={{ background: c + "12", color: c, borderColor: c + "33" }}><span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />{s}</span>;
}

const TABS = ["Release Gate", "Data Flow Map", "Permissible Purpose", "Disputes", "Registries"];

export default function GovernanceCenter() {
  const [tab, setTab] = useState("Release Gate");
  const [gate, setGate] = useState(null);
  const [flow, setFlow] = useState(null);
  const [purposes, setPurposes] = useState(null);
  const [disputes, setDisputes] = useState(null);
  const [reg, setReg] = useState(null);
  const [pForm, setPForm] = useState({ consumer: "", purpose: "", product: "", provider: "" });
  const [dForm, setDForm] = useState({ subject: "", category: "Accuracy", provider: "" });

  const loadPurposes = useCallback(() => api.get("/governance/permissible-purpose").then(({ data }) => setPurposes(data)).catch(() => {}), []);
  const loadDisputes = useCallback(() => api.get("/governance/disputes").then(({ data }) => setDisputes(data.items)).catch(() => {}), []);

  useEffect(() => {
    api.get("/governance/overview").then(({ data }) => setGate(data)).catch(() => {});
    api.get("/governance/data-flow-map").then(({ data }) => setFlow(data)).catch(() => {});
    api.get("/governance/registries").then(({ data }) => setReg(data)).catch(() => {});
    loadPurposes(); loadDisputes();
  }, [loadPurposes, loadDisputes]);

  const addPurpose = async () => {
    if (!pForm.purpose || !pForm.product || !pForm.provider) return toast.error("Fill purpose, product and provider");
    try { await api.post("/governance/permissible-purpose", pForm); toast.success("Permissible purpose recorded"); setPForm({ ...pForm, product: "", provider: "" }); loadPurposes(); }
    catch { toast.error("Could not record"); }
  };
  const addDispute = async () => {
    if (!dForm.subject) return toast.error("Enter a subject");
    try { await api.post("/governance/disputes", dForm); toast.success("Dispute case opened"); setDForm({ subject: "", category: "Accuracy", provider: "" }); loadDisputes(); }
    catch { toast.error("Could not open case"); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/governance/disputes/${id}`, { status }); loadDisputes(); } catch { toast.error("Failed"); }
  };

  if (!gate) return <Loading />;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="governance-page">
      <PageHeader eyebrow="Compliance & Governance Center" title="Audit Release Gate"
        subtitle="Evidence-based control posture across security, consent, permissible purpose, disputes, vendor risk, AI governance and provider credentialing. Statuses reflect the true state — nothing is overclaimed." />

      {/* honest gate banner */}
      <Card className="border-amber-200 bg-amber-50/50 p-5" data-testid="gate-banner">
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="mt-0.5 shrink-0 text-amber-600" />
          <div>
            <div className="flex items-center gap-2"><span className="font-head text-lg font-extrabold text-slate-900">{gate.gate}</span><Chip s="PROVIDER APPROVAL PENDING" /></div>
            <p className="mt-1 text-[13px] text-slate-600">{gate.gate_reason}</p>
            <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500">
              <span className="rounded bg-white px-2 py-0.5">Implemented/Functional: <b>{gate.summary.implemented_or_functional}</b></span>
              <span className="rounded bg-white px-2 py-0.5">Documented: <b>{gate.summary.documented}</b></span>
              <span className="rounded bg-white px-2 py-0.5">Partial: <b>{gate.summary.partial}</b></span>
              <span className="rounded bg-white px-2 py-0.5">Not started: <b>{gate.summary.not_started}</b></span>
              <span className="rounded bg-white px-2 py-0.5">Provider pending: <b>{gate.summary.provider_pending}</b></span>
            </div>
          </div>
        </div>
      </Card>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`gov-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
            className={`rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors ${tab === t ? "bg-[#080d1a] text-white" : "border border-slate-200 bg-white text-slate-500"}`}>{t}</button>
        ))}
      </div>

      {tab === "Release Gate" && (
        <div className="grid gap-3 md:grid-cols-2">
          {gate.areas.map((a) => (
            <Card key={a.area} className="p-4" data-testid="gate-area">
              <div className="flex items-center justify-between"><span className="font-head text-[14px] font-bold text-slate-900">{a.area}</span><Chip s={a.status} /></div>
              <div className="mt-2"><Bar pct={a.score} tone={a.score >= 75 ? "#059669" : a.score >= 45 ? "#0369a1" : "#dc2626"} /></div>
              <p className="mt-2 text-[12px] text-slate-600">{a.evidence}</p>
              <p className="mt-1 text-[11px] text-slate-400">{a.note}</p>
            </Card>
          ))}
        </div>
      )}

      {tab === "Data Flow Map" && flow && (
        <Card className="p-5" data-testid="data-flow-map">
          <SectionTitle icon={GitBranch}>Data Flow Map</SectionTitle>
          <div className="mb-4 flex flex-wrap items-center gap-1.5">
            {flow.stages.map((s, i) => (
              <div key={s} className="flex items-center gap-1.5">
                <span className="rounded-md bg-[#080d1a] px-2.5 py-1 font-mono text-[10px] font-bold text-white">{s}</span>
                {i < flow.stages.length - 1 && <span className="text-slate-300">→</span>}
              </div>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead><tr className="border-b border-slate-100 text-left font-mono text-[9px] uppercase tracking-wider text-slate-400">
                <th className="pb-2 pr-3">Data Category</th><th className="pb-2 pr-3">Class</th><th className="pb-2 pr-3">Provider</th><th className="pb-2 pr-3">AI Allowed</th><th className="pb-2">State</th>
              </tr></thead>
              <tbody>
                {flow.categories.map((c) => (
                  <tr key={c.category} className="border-b border-slate-50" data-testid="flow-row">
                    <td className="py-2 pr-3 font-medium text-slate-800">{c.category}</td>
                    <td className="py-2 pr-3"><Chip s={c.data_class} /></td>
                    <td className="py-2 pr-3 text-slate-600">{c.provider}</td>
                    <td className="py-2 pr-3">{c.ai_allowed ? <span className="text-emerald-600">Yes</span> : <span className="font-semibold text-red-600">No</span>}</td>
                    <td className="py-2"><Chip s={c.state} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-[11px] text-slate-400">{flow.note}</p>
        </Card>
      )}

      {tab === "Permissible Purpose" && purposes && (
        <div className="grid gap-5 lg:grid-cols-3">
          <Card className="p-5">
            <SectionTitle icon={FileCheck2}>Record a Permissible Purpose</SectionTitle>
            <p className="mb-3 text-[12px] text-slate-500">A consumer report cannot be requested without a recorded FCRA basis.</p>
            <div className="space-y-2">
              <input value={pForm.consumer} onChange={(e) => setPForm({ ...pForm, consumer: e.target.value })} placeholder="Consumer" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="purpose-consumer" />
              <select value={pForm.purpose} onChange={(e) => setPForm({ ...pForm, purpose: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="purpose-select">
                <option value="">Select permissible purpose…</option>
                {purposes.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <input value={pForm.product} onChange={(e) => setPForm({ ...pForm, product: e.target.value })} placeholder="Requesting product (e.g. LOC)" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="purpose-product" />
              <input value={pForm.provider} onChange={(e) => setPForm({ ...pForm, provider: e.target.value })} placeholder="Provider (e.g. TransUnion)" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="purpose-provider" />
              <button onClick={addPurpose} data-testid="purpose-add-btn" className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#1a5fd0] py-2 text-[13px] font-semibold text-white"><Plus size={14} /> Record purpose</button>
            </div>
          </Card>
          <Card className="p-5 lg:col-span-2">
            <SectionTitle icon={FileCheck2}>Permissible Purpose Registry</SectionTitle>
            <div className="space-y-2">
              {purposes.items.length === 0 && <div className="py-6 text-center text-sm text-slate-400">No purposes recorded yet.</div>}
              {purposes.items.map((p) => (
                <div key={p.id} className="rounded-lg border border-slate-100 px-4 py-3" data-testid="purpose-row">
                  <div className="flex items-center justify-between"><span className="text-[13px] font-semibold text-slate-800">{p.purpose}</span><Chip s={p.state} /></div>
                  <div className="mt-0.5 font-mono text-[11px] text-slate-400">{p.consumer} · {p.product} · {p.provider} · {p.created_at?.slice(0, 10)}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {tab === "Disputes" && disputes && (
        <div className="grid gap-5 lg:grid-cols-3">
          <Card className="p-5">
            <SectionTitle icon={AlertTriangle}>Open a Dispute Case</SectionTitle>
            <div className="space-y-2">
              <input value={dForm.subject} onChange={(e) => setDForm({ ...dForm, subject: e.target.value })} placeholder="Subject" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="dispute-subject" />
              <select value={dForm.category} onChange={(e) => setDForm({ ...dForm, category: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="dispute-category">
                {["Accuracy", "Reinvestigation", "Correction", "Complaint", "Furnisher escalation"].map((c) => <option key={c}>{c}</option>)}
              </select>
              <input value={dForm.provider} onChange={(e) => setDForm({ ...dForm, provider: e.target.value })} placeholder="Provider (optional)" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="dispute-provider" />
              <button onClick={addDispute} data-testid="dispute-add-btn" className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#1a5fd0] py-2 text-[13px] font-semibold text-white"><Plus size={14} /> Open case</button>
            </div>
          </Card>
          <Card className="p-5 lg:col-span-2">
            <SectionTitle icon={AlertTriangle}>Dispute & Accuracy Cases</SectionTitle>
            <div className="space-y-2">
              {disputes.length === 0 && <div className="py-6 text-center text-sm text-slate-400">No dispute cases.</div>}
              {disputes.map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3" data-testid="dispute-row">
                  <div><div className="text-[13px] font-semibold text-slate-800">{d.subject}</div><div className="font-mono text-[11px] text-slate-400">{d.category}{d.provider ? ` · ${d.provider}` : ""}</div></div>
                  <div className="flex items-center gap-2">
                    <Chip s={d.status} />
                    {d.status !== "RESOLVED" && <button onClick={() => setStatus(d.id, "RESOLVED")} data-testid="dispute-resolve-btn" className="rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600">Resolve</button>}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {tab === "Registries" && reg && (
        <div className="space-y-5">
          <Card className="p-5">
            <SectionTitle icon={Cpu}>AI Provider Governance</SectionTitle>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {reg.ai_governance.approved_models.map((m) => <span key={m.capability} className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[10px] text-slate-600">{m.capability} → {m.provider}:{m.model}</span>)}
            </div>
            <ul className="space-y-1 text-[12px] text-slate-600">{reg.ai_governance.rules.map((r) => <li key={r}>• {r}</li>)}</ul>
          </Card>
          <Card className="p-5">
            <SectionTitle icon={Building2}>Vendor Risk Register</SectionTitle>
            <div className="grid gap-2 sm:grid-cols-2">
              {reg.vendors.map((v) => (
                <div key={v.vendor} className="rounded-lg border border-slate-100 px-3 py-2.5" data-testid="vendor-row">
                  <div className="flex items-center justify-between"><span className="text-[13px] font-semibold text-slate-800">{v.vendor}</span><Chip s={v.state} /></div>
                  <div className="mt-0.5 font-mono text-[10px] text-slate-400">{v.capability} · risk {v.risk} · DPA {v.dpa} · breach {v.breach_sla}</div>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-5">
            <SectionTitle icon={Megaphone}>Product Claim Registry</SectionTitle>
            <div className="space-y-1.5">
              {reg.claims.map((c) => (
                <div key={c.claim} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
                  <div><span className="text-[13px] font-medium text-slate-800">"{c.claim}"</span><div className="text-[11px] text-slate-400">{c.evidence}</div></div>
                  <Chip s={c.status} />
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-5">
            <SectionTitle icon={ShieldCheck}>Operational Controls</SectionTitle>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {reg.controls.map((c) => (
                <div key={c.name} className="rounded-lg border border-slate-100 px-3 py-2.5">
                  <div className="flex items-center justify-between"><span className="text-[12px] font-semibold text-slate-800">{c.name}</span><Chip s={c.status} /></div>
                  <div className="mt-0.5 text-[11px] text-slate-400">{c.detail}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
